/**
 * "Not published by this platform version" — the designed absence of a route the pinned
 * client knows and the running platform does not serve yet (`docs/adr/6004`).
 *
 * #148 Phase 2 pins the client to bata-platform main, which carries the alert dispositions
 * (ADR 2004), the widened decision summary (ADR 6036) and the execution loop (ADR 6037).
 * Production answers those routes 404 until platform v0.47 deploys, and this build will be in
 * production first. A planner told every morning to report a fault that is a deployment
 * calendar would stop reading the notices — so a missing route renders as an information
 * line, never an error banner, following the cycle-status band (`docs/adr/9016`).
 *
 * **Which 404s are the route's absence.** A route that does not exist answers FastAPI's own
 * `{"detail": "Not Found"}` (no problem document — `ApiMalformedProblemError`) or the
 * skeleton's `platform.not_found`. The routes themselves publish 404s of their own —
 * `planning.inseason.run_not_found`, `planning.inseason.write_back_command_not_found`,
 * `planning.foundations.alert_not_found` — and those are refusals about THIS request, not
 * about the platform's version, so a `planning.*` code is never read as "not published".
 * 501 is the same statement from a platform that registered the route and built nothing.
 */

import { ApiMalformedProblemError, ApiProblemError } from '@/api';

export function isNotPublished(error: unknown): boolean {
  if (error instanceof ApiMalformedProblemError) return error.status === 404 || error.status === 501;
  if (!(error instanceof ApiProblemError)) return false;
  if (error.status === 501) return true;
  return error.status === 404 && !error.code.startsWith('planning.');
}

/**
 * A field the running platform does not publish yet, on a response it does.
 *
 * The decision summary itself is served by production today (ADR 6030); ADR 6036's fields on it
 * are not, and the generated type declares them required because the pinned spec does. So their
 * presence is tested at runtime, where the type cannot: reading `summary.alerts_open` off a
 * v0.46 answer is `undefined` with a type that says `number`.
 */
export function hasField(value: object, field: string): boolean {
  return (value as Readonly<Record<string, unknown>>)[field] !== undefined;
}
